import cookies from "browser-cookies"
import { nostojs } from "./api"

const storage = {
  get: (key: string) => {
    const valueFromSession = sessionStorage.getItem(key)
    if (valueFromSession) {
      return valueFromSession
    }
    // initial fallback to cookies
    const valueFromCookie = cookies.get(key)
    if (valueFromCookie) {
      sessionStorage.setItem(key, valueFromCookie)
      return valueFromCookie
    }
    return null
  },
  set: (key: string, value: string) => {
    sessionStorage.setItem(key, value)
  }
}

const overlayHeight = "85px"

// Module-level closePreview function
function closePreview() {
  sessionStorage.removeItem("nostomarketspreview")
  const url = new URL(window.location.href)
  url.searchParams.delete("nostomarketspreview")
  window.location.href = url.toString()
}

// Custom element class for Nosto Markets Preview
class NostoMarketsPreview extends HTMLElement {
  connectedCallback() {
    this.attachShadow({ mode: "open" })
    this.setupShadowDOM()
    this.setupEventListeners()
  }

  private setupShadowDOM() {
    // Get server and market language info from attributes
    const nostoServer = this.dataset.nostoServer || ""
    const marketLanguage = this.dataset.marketLanguage || ""

    const content = `
      <style>
        :host {
          font-family: Roboto Mono, sans-serif;
          height: ${overlayHeight};
          margin: 0 10px 0 10px;
          position: fixed;
          bottom: 25px;
          z-index: 2147483647;
          width: 50%;
          border: none;
          overflow: hidden;
          transform: translate(50%, 0%);
          display: block;
        }
        
        .container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #F8F8F8;
          height: ${overlayHeight};
          animation: drop-in 1s ease 200ms backwards;
          background: rgba(24, 19, 44, 0.565);
          padding: 0 10px 0 10px;
          border-radius: 5px;
        }
        
        .overlay-logo {
          display: flex;
          height: auto;
          width: 15%;
          padding: 10px;
        }
        
        .title {
          font-weight: bold;
          display: inherit;
          height: auto;
          padding: 0;
          margin: 0;
        }
        
        .close-preview-button {
          padding: 10px 10px;
          background-color: #6761E7;
          width: 10em;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          color: #F8F8F8;
          font-size: 16px;
          font-weight: bold;
          text-align: center;
        }
        
        @keyframes drop-in {
          from {
            opacity: 0;
            transform: translateY(-100px);
          }
          to {
            opacity: 1;
            transform: translate(0px);
          }
       }
      </style>
      <div class="container">
        <img src="${nostoServer}/assets/public/images/foundation5/logo-nosto-inverse.png" alt="Nosto" class="overlay-logo">
        <h3 class="title">Shopify Markets Preview ${marketLanguage}</h3>
        <button class="close-preview-button"><b>Close preview</b></button>
      </div>
    `

    this.shadowRoot!.innerHTML = content
  }

  private setupEventListeners() {
    const closeButton = this.shadowRoot!.querySelector(".close-preview-button")
    if (closeButton) {
      closeButton.addEventListener("click", closePreview)
    }
  }
}

if (!customElements.get("nosto-markets-preview")) {
  customElements.define("nosto-markets-preview", NostoMarketsPreview)
}

// Declare the custom element interface for global usage
declare global {
  interface HTMLElementTagNameMap {
    "nosto-markets-preview": NostoMarketsPreview
  }
}

export default () => {
  nostojs(api => {
    const nostoSettings = api.internal.getSettings()

    // Prepare market language text
    let marketLanguage = ""
    if (window.Shopify?.locale && window.Shopify?.country) {
      const { country, locale } = window.Shopify
      marketLanguage = `| ${country.toUpperCase()} | ${locale.toUpperCase()}`
    }

    if (storage.get("nostomarketspreview") === "true") {
      // Remove any existing preview element
      document.querySelector("nosto-markets-preview")?.remove()

      // Create and configure the custom element
      const nostoMarketsPreviewElement = document.createElement("nosto-markets-preview")
      nostoMarketsPreviewElement.setAttribute("data-nosto-server", nostoSettings.server)
      nostoMarketsPreviewElement.setAttribute("data-market-language", marketLanguage)
      document.body.appendChild(nostoMarketsPreviewElement)
    } else {
      document.querySelector("nosto-markets-preview")?.remove()
    }
    return true
  })
}
